// ----------------------------------------------------------------------------
// Spring Calculator: Matcher
// ----------------------------------------------------------------------------
//
// Turns a recommended spring rate into purchasable spring variants.
//
// The catalogue (spring-calculator-data.json) lists FOX spring part numbers
// with the stroke range, rate and total length (TLG) of each. Store variants
// are matched to catalogue rows by SKU, so the same data file works on every
// store as long as the spring product SKUs equal the FOX part numbers.
//
// ----------------------------------------------------------------------------

const normaliseSku = (sku) => String(sku || '').trim().toUpperCase();

/**
 * Max total length (inches) a shock model accepts for a stroke, or null if the
 * model has no entry covering that stroke.
 */
export const getMaxTlg = (shockModel, strokeMm) => {
    if (!shockModel) return null;
    const range = shockModel.tlg.find((row) => strokeMm >= row.minStroke && strokeMm <= row.maxStroke);
    return range ? range.maxTlg : null;
};

/**
 * Catalogue springs that fit the stroke, deliver the rate and are short enough
 * for the shock model.
 */
export const findCompatibleSprings = (springs, { strokeMm, rate, maxTlg }) => {
    if (maxTlg === null) return [];

    return springs.filter((spring) => (
        strokeMm >= spring.minStroke &&
        strokeMm <= spring.maxStroke &&
        spring.rate === rate &&
        spring.maxTlg <= maxTlg
    ));
};

/**
 * Index store variants by SKU so catalogue rows can be resolved in O(1).
 */
export const indexVariantsBySku = (variants) => variants.reduce((index, variant) => {
    const sku = normaliseSku(variant.sku);
    if (sku && !index[sku]) index[sku] = variant;
    return index;
}, {});

/**
 * Resolve compatible catalogue springs to store variants. Springs the store does
 * not sell are dropped; the same part number is only returned once.
 *
 * @returns {Array<{ spring: Object, variant: Object }>}
 */
export const resolveSpringVariants = (springs, variantIndex) => {
    const seen = new Set();

    return springs.reduce((matches, spring) => {
        const sku = normaliseSku(spring.partNumber);
        const variant = variantIndex[sku];

        if (variant && !seen.has(sku)) {
            seen.add(sku);
            matches.push({ spring, variant });
        }
        return matches;
    }, []);
};
